import { Platform, StyleSheet, Text, View } from 'react-native'
import { formatGBPFromPence } from '../format'
import { colors, fontSize, fontWeight, radius, space } from '../tokens'

type Props = {
  priceAmountPence: number
  receiptUrl?: string | null
  variant?: 'ready' | 'pending-details'
}

const ShieldCheck = () =>
  Platform.OS === 'web' ? (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      width={26}
      height={26}
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ color: '#fff' }}
    >
      <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
      <path d="M9 12l2 2 4-4" />
    </svg>
  ) : (
    <Text style={styles.iconFallback}>✓</Text>
  )

export const FulfilmentConfirmBanner = ({
  priceAmountPence,
  receiptUrl,
  variant = 'ready',
}: Props) => {
  const title =
    variant === 'ready'
      ? 'Payment confirmed — your pack is ready'
      : 'Payment confirmed — last detail before we send'
  const sub =
    variant === 'ready'
      ? 'Your evidence and Band Review email are written and waiting. Just send them.'
      : 'Tell us your name so we can put it on the email to the Valuation Office.'
  // Route through the shared formatter so sub-pound prices render exactly
  // (£4.99 paid), not rounded to the pound — this is a receipt-adjacent surface.
  const price = `${formatGBPFromPence(priceAmountPence)} paid`

  return (
    <View style={styles.banner}>
      <View style={styles.icon}>
        <ShieldCheck />
      </View>
      <View style={styles.copy}>
        <Text style={styles.title}>{title}</Text>
        <Text style={styles.sub}>{sub}</Text>
      </View>
      <View style={styles.receipt}>
        <Text style={styles.price}>{price}</Text>
        {receiptUrl && Platform.OS === 'web' ? (
          <a
            href={receiptUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{ fontSize: 12, color: colors.positiveInk, opacity: 0.8 }}
          >
            Receipt
          </a>
        ) : (
          <Text style={styles.receiptText}>Receipt emailed</Text>
        )}
      </View>
    </View>
  )
}

const styles = StyleSheet.create({
  banner: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space[16],
    padding: 18,
    paddingHorizontal: 22,
    borderRadius: radius.sm,
    backgroundColor: colors.positiveSoft,
  },
  icon: {
    width: 46,
    height: 46,
    borderRadius: 12,
    backgroundColor: colors.positive,
    alignItems: 'center',
    justifyContent: 'center',
  },
  iconFallback: { color: '#fff', fontSize: 22, fontWeight: '800' },
  copy: { flex: 1, minWidth: 0 },
  title: {
    fontSize: 19,
    fontWeight: fontWeight.bold,
    color: colors.positiveInk,
    marginBottom: 3,
    letterSpacing: -0.3,
  },
  sub: { fontSize: 13.5, color: colors.positiveInk, opacity: 0.85, lineHeight: 19 },
  receipt: { alignItems: 'flex-end', gap: 2 },
  price: {
    fontSize: fontSize.small,
    fontWeight: fontWeight.bold,
    color: colors.positiveInk,
  },
  receiptText: { fontSize: 12, color: colors.positiveInk, opacity: 0.8 },
})
